/**
 * 十進位字串（批151）——後端 `services/decimal_str.py` 的**逐字鏡像**。
 *
 * 產品參數版本的值是換算率、單價這類會被乘進金額的數字，協定上**一律是字串**
 * （JSON number 會被解成浮點，`1.50` 與 `1.5` 也分不出來）。形狀由後端唯一定義，
 * 這裡只是讓操作者在按下儲存之前就看到紅字；權威在後端（422 `invalid_value`）。
 * ⚠️ 規則改了兩邊一起改（`decimalStr.spec.ts` 釘住同一組正反例）。
 *
 * - 非負、整數部分 1～12 位、小數部分 1～6 位；不收正負號、科學記號、空白、千分位、
 *   只有小數點（`1.`／`.5`）。
 * - ★ 刻意寫 `[0-9]` 而不是 `\d`（後端的理由是 Python 的 `\d` 會匹配全形數字；
 *   JavaScript 的 `\d` 雖然只認 ASCII，兩邊寫成同一個字面比較好對照）。
 * - 標準形：去整數部分的前導 0、去小數部分的尾 0、小數清空就連小數點一起去掉。
 */

export const MAX_INT_DIGITS = 12
export const MAX_FRAC_DIGITS = 6
/** 形狀（與後端 `PATTERN` 逐字相同）。 */
export const PATTERN = '^[0-9]{1,12}(\\.[0-9]{1,6})?$'

const RE = new RegExp(PATTERN)

/** 後端 `DecimalStrError` 的那句說明（給表單紅字用）。 */
export const DECIMAL_HINT =
  `必須是非負的十進位數字：整數最多 ${MAX_INT_DIGITS} 位、小數最多 ${MAX_FRAC_DIGITS} 位` +
  '（不可有正負號、科學記號、空白或千分位）'

/** `value` 是不是合法的十進位字串（只收字串，不代為轉型）。 */
export function isDecimalStr(value: unknown): value is string {
  return typeof value === 'string' && RE.test(value)
}

/** 驗證並回標準形；不合法回 `null`（呼叫端自己決定錯誤訊息）。 */
export function canonicalDecimal(value: unknown): string | null {
  if (!isDecimalStr(value)) return null
  const dot = value.indexOf('.')
  const intPart = (dot < 0 ? value : value.slice(0, dot)).replace(/^0+/, '') || '0'
  const frac = dot < 0 ? '' : value.slice(dot + 1).replace(/0+$/, '')
  return frac ? `${intPart}.${frac}` : intPart
}
